import { createFileRoute } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Rss, ExternalLink, Copy, AlertCircle, AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";
import { FeedRefreshHistoryCard } from "@/components/admin/FeedRefreshHistoryCard";
import { FeedSpecCard } from "@/components/admin/FeedSpecCard";
import { FeedPreflightCard } from "@/components/admin/FeedPreflightCard";
import { FeedPreviewDownloadCard } from "@/components/admin/FeedPreviewDownloadCard";
import {
  MetaFormatHelpTooltip,
  MetaFormatFieldsDocs,
} from "@/components/admin/MetaFormatFieldsDocs";
import { DealerGeoDiagnosticsCard } from "@/components/admin/DealerGeoDiagnosticsCard";
import { JsonLdDebugCard } from "@/components/admin/JsonLdDebugCard";
import {
  FACEBOOK_FEED_FORMATS,
  DEFAULT_FACEBOOK_FEED_FORMAT,
  parseFacebookFeedFormat,
  type FacebookFeedFormat,
  META_CATALOG_VERTICALS,
  DEFAULT_META_CATALOG_VERTICAL,
  parseMetaCatalogVertical,
  checkFormatVerticalCompatibility,
  type MetaCatalogVertical,
} from "@/lib/feed-format";

export const Route = createFileRoute("/admin/feeds")({
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  component: () => (<AdminOnlyGuard><AdminFeeds /></AdminOnlyGuard>),
});

type FeedSettings = {
  domain: string;
  shopName: string;
  googleEnabled: boolean;
  facebookEnabled: boolean;
  facebookFormat: FacebookFeedFormat;
  /** What the admin says their Meta Commerce Manager catalog vertical is.
   *  Drives the format/vertical guard — see checkFormatVerticalCompatibility. */
  metaCatalogVertical: MetaCatalogVertical;
  /** Google Shopping product category ID (taxonomy). Default 916 = Cars. */
  googleProductCategory: string;
  /** Brand fallback used when a row has no `brand` set. */
  defaultBrand: string;
  /** Path template for the landing URL, must contain `{slug}`. */
  landingPathTemplate: string;
  /** Feed names whose recurring-failure email alert is suppressed. Used when
   *  the admin only runs ads on a subset of feeds (e.g. only -retail) and
   *  doesn't care that a legacy/unused feed is failing validation. */
  silencedAlertFeeds: string[];
};

const defaultSettings: FeedSettings = {
  domain: "autoxchange.dk",
  shopName: "AutoXChange - Biler til salg",
  googleEnabled: true,
  facebookEnabled: true,
  facebookFormat: DEFAULT_FACEBOOK_FEED_FORMAT,
  metaCatalogVertical: DEFAULT_META_CATALOG_VERTICAL,
  googleProductCategory: "916",
  defaultBrand: "",
  landingPathTemplate: "/biler-til-salg/{slug}",
  silencedAlertFeeds: [],
};

// Order matches the FEEDS array in refresh-feeds.ts.
const ALERTABLE_FEEDS: { name: string; label: string; description: string }[] = [
  { name: "google", label: "Google Shopping", description: "/feeds/google" },
  {
    name: "facebook",
    label: "Facebook (AIA / Vehicles)",
    description: "/feeds/facebook — legacy AIA/Vehicles katalog",
  },
  {
    name: "facebook-retail",
    label: "Facebook Retail",
    description: "/feeds/facebook-retail — Retail/Vehicle Inventory katalog",
  },
];

function AdminFeeds() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<FeedSettings>(defaultSettings);
  /** Admin override: lets you save a vertical/format mismatch on purpose
   *  (e.g. while migrating between catalog types). Resets every time the
   *  active mismatch changes so you have to reconfirm per attempt. */
  const [overrideMismatch, setOverrideMismatch] = useState(false);

  const { data: savedSettings, isLoading } = useQuery({
    queryKey: ["feed-settings"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "feeds")
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (savedSettings?.value) {
      const val = savedSettings.value as Partial<FeedSettings> & {
        facebookFormat?: unknown;
        metaCatalogVertical?: unknown;
        silencedAlertFeeds?: unknown;
      };
      const silenced = Array.isArray(val.silencedAlertFeeds)
        ? val.silencedAlertFeeds.filter((x): x is string => typeof x === "string")
        : [];
      setSettings({
        ...defaultSettings,
        ...val,
        facebookFormat: parseFacebookFeedFormat(val.facebookFormat),
        metaCatalogVertical: parseMetaCatalogVertical(val.metaCatalogVertical),
        silencedAlertFeeds: silenced,
      });
    }
  }, [savedSettings]);

  // Re-evaluate compatibility on every change. Memoised so dropdown labels
  // don't recompute on unrelated keystrokes.
  const compatibility = useMemo(
    () =>
      checkFormatVerticalCompatibility(
        settings.metaCatalogVertical,
        settings.facebookFormat,
      ),
    [settings.metaCatalogVertical, settings.facebookFormat],
  );

  // Reset the "I know what I'm doing" override whenever the (vertical, format)
  // pair changes — admins must re-confirm per intentional mismatch.
  useEffect(() => {
    setOverrideMismatch(false);
  }, [settings.metaCatalogVertical, settings.facebookFormat]);

  const isBlocked = compatibility.status === "block" && !overrideMismatch;




  const saveMutation = useMutation({
    mutationFn: async () => {
      // Defense-in-depth: even if the UI button is somehow clicked while
      // a hard mismatch is active, refuse to write a known-broken combo
      // to site_settings. Surfaced as a toast via onError below.
      if (compatibility.status === "block" && !overrideMismatch) {
        throw new Error(
          `Feed-format "${settings.facebookFormat}" matcher ikke kataloggets vertical "${settings.metaCatalogVertical}" — sæt flueben i overstyringen for at gemme alligevel.`,
        );
      }
      const jsonValue = JSON.parse(JSON.stringify(settings));
      if (savedSettings) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value: jsonValue })
          .eq("key", "feeds");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert([{ key: "feeds", value: jsonValue }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feed-settings"] });
      toast.success("Feed-indstillinger gemt!");
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Kunne ikke gemme indstillinger";
      toast.error(msg);
    },
  });

  const copyUrl = (path: string) => {
    const url = `https://${settings.domain}${path}`;
    navigator.clipboard.writeText(url);
    toast.success("URL kopieret!");
  };

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Indlæser...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground">Feed-indstillinger</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Konfigurer Google Shopping og Facebook Catalog feeds
      </p>

      {/* General settings */}
      <div className="mt-8 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <h2 className="text-lg font-semibold text-foreground">Generelt</h2>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Domæne</label>
            <Input
              value={settings.domain}
              onChange={(e) => setSettings({ ...settings, domain: e.target.value })}
              placeholder="autoxchange.dk"
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Domænet bruges i feed-links til bilerne
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">Butiksnavn</label>
            <Input
              value={settings.shopName}
              onChange={(e) => setSettings({ ...settings, shopName: e.target.value })}
              placeholder="AutoXChange - Biler til salg"
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Vises som titel i feed-outputtet
            </p>
          </div>
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || isBlocked}
          className="mt-6 bg-accent text-accent-foreground hover:brightness-110 disabled:opacity-50"
          title={
            isBlocked
              ? "Feed-format matcher ikke kataloggets vertical — sæt flueben i overstyringen for at gemme alligevel."
              : undefined
          }
        >
          <Save size={16} className="mr-2" />
          {saveMutation.isPending ? "Gemmer..." : "Gem indstillinger"}
        </Button>
      </div>

      {/* Kategori, brand & landings-URL */}
      <div className="mt-6 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <h2 className="text-lg font-semibold text-foreground">
          Kategori, brand &amp; landings-URL
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Styrer Google Shopping-kategori, brand-fallback og hvilken URL hver
          bil i feedet linker til.
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Google product category (taxonomy ID)
            </label>
            <Input
              value={settings.googleProductCategory}
              onChange={(e) =>
                setSettings({ ...settings, googleProductCategory: e.target.value })
              }
              placeholder="916"
              className="mt-1.5"
              inputMode="numeric"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Standard: <code>916</code> (Vehicles &amp; Parts &gt; Motor Vehicles &gt; Cars).
              Skift hvis dit Merchant Center-katalog kræver en anden taxonomy-ID.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">
              Brand-fallback
            </label>
            <Input
              value={settings.defaultBrand}
              onChange={(e) =>
                setSettings({ ...settings, defaultBrand: e.target.value })
              }
              placeholder="(tom = brug kun bilens egen brand)"
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Bruges kun når en bil mangler <code>brand</code>. Lad stå tom for
              at fejle åbenlyst i preview-validering i stedet.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">
              Landings-URL skabelon
            </label>
            <Input
              value={settings.landingPathTemplate}
              onChange={(e) =>
                setSettings({ ...settings, landingPathTemplate: e.target.value })
              }
              placeholder="/biler-til-salg/{slug}"
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Skal indeholde <code>{"{slug}"}</code> — erstattes af bilens{" "}
              <code>seo_slug</code> (eller id som fallback). Eksempel-URL:{" "}
              <code>
                https://{settings.domain}
                {settings.landingPathTemplate.replace("{slug}", "audi-a4-2024")}
              </code>
            </p>
          </div>
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={
            saveMutation.isPending ||
            isBlocked ||
            !settings.landingPathTemplate.includes("{slug}")
          }
          className="mt-6 bg-accent text-accent-foreground hover:brightness-110 disabled:opacity-50"
          title={
            !settings.landingPathTemplate.includes("{slug}")
              ? "Skabelonen skal indeholde {slug}"
              : undefined
          }
        >
          <Save size={16} className="mr-2" />
          {saveMutation.isPending ? "Gemmer..." : "Gem feed-felter"}
        </Button>
      </div>

      {/* Facebook feed format selector */}
      <div className="mt-6 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <h2 className="text-lg font-semibold text-foreground">
          Facebook feed-format
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Vælg hvilket skema <code className="rounded bg-muted px-1">/feeds/facebook</code>{" "}
          skal udsende. Skal matche kataloggets <em>vertical</em> i Meta
          Commerce Manager — vælges forkert format afvises hele uploaden.
        </p>

        <div className="mt-4">
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-foreground">Format</label>
            <MetaFormatHelpTooltip />
          </div>
          <Select
            value={settings.facebookFormat}
            onValueChange={(v) =>
              setSettings({
                ...settings,
                facebookFormat: parseFacebookFeedFormat(v),
              })
            }
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FACEBOOK_FEED_FORMATS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active-format description */}
          <div className="mt-3 rounded-lg bg-muted p-3">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {
                FACEBOOK_FEED_FORMATS.find(
                  (f) => f.value === settings.facebookFormat,
                )?.description
              }
            </p>
          </div>

          {settings.facebookFormat === "retail" ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-destructive">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <p className="text-xs leading-relaxed">
                <strong>Advarsel:</strong> Retail RSS-format anbefales ikke til
                bilforhandlere. Meta vil typisk afvise bil-specifikke felter
                (mileage, body_style, vin). Brug kun denne hvis dit katalog er
                en almindelig e-handelskatalog.
              </p>
            </div>
          ) : null}
        </div>

        {/* --- Vertical guard ------------------------------------------- */}
        <div className="mt-6 border-t border-border pt-6">
          <label className="text-sm font-medium text-foreground">
            Meta Commerce Manager: katalog-vertical
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            Hvad har du valgt som <em>vertical</em> da kataloget blev oprettet i
            Meta Commerce Manager? Bruges til at blokere gem hvis feed-formatet
            ovenfor ikke matcher.
          </p>
          <Select
            value={settings.metaCatalogVertical}
            onValueChange={(v) =>
              setSettings({
                ...settings,
                metaCatalogVertical: parseMetaCatalogVertical(v),
              })
            }
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {META_CATALOG_VERTICALS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 rounded-lg bg-muted p-3">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {
                META_CATALOG_VERTICALS.find(
                  (v) => v.value === settings.metaCatalogVertical,
                )?.description
              }
            </p>
          </div>

          <CompatibilityBanner
            status={compatibility.status}
            message={compatibility.message}
            recommendedFormat={compatibility.recommendedFormat}
            onApplyRecommended={() => {
              if (compatibility.recommendedFormat) {
                setSettings({
                  ...settings,
                  facebookFormat: compatibility.recommendedFormat,
                });
              }
            }}
          />

          {compatibility.status === "block" ? (
            <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
              <Checkbox
                checked={overrideMismatch}
                onCheckedChange={(c) => setOverrideMismatch(c === true)}
                className="mt-0.5"
              />
              <span className="text-xs leading-relaxed text-foreground">
                <strong>Overstyr blokering:</strong> Jeg ved, at jeg gemmer en
                kombination der vil få Meta til at afvise alle rækker
                (f.eks. mid-migrering mellem katalogtyper).
              </span>
            </label>
          ) : null}
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || isBlocked}
          className="mt-6 bg-accent text-accent-foreground hover:brightness-110 disabled:opacity-50"
          title={
            isBlocked
              ? "Feed-format matcher ikke kataloggets vertical — sæt flueben i overstyringen for at gemme alligevel."
              : undefined
          }
        >
          <Save size={16} className="mr-2" />
          {saveMutation.isPending ? "Gemmer..." : "Gem feed-format"}
        </Button>
      </div>

      {/* Docs: per-format Meta field reference */}
      <div className="mt-6 max-w-4xl">
        <MetaFormatFieldsDocs activeFormat={settings.facebookFormat} />
      </div>

      {/* Alert silencing — suppress recurring-failure emails per feed */}
      <div className="mt-6 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <h2 className="text-lg font-semibold text-foreground">
          Feed-alarmer
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Slå email-advarsler fra for feeds du ikke længere bruger. Feedet
          tjekkes stadig og logges i historikken — du får bare ikke email
          ved gentagne fejl.
        </p>

        <div className="mt-4 space-y-3">
          {ALERTABLE_FEEDS.map((feed) => {
            const silenced = settings.silencedAlertFeeds.includes(feed.name);
            return (
              <label
                key={feed.name}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-background p-3 hover:bg-muted/40"
              >
                <Checkbox
                  checked={silenced}
                  onCheckedChange={(c) => {
                    const next = c === true
                      ? Array.from(new Set([...settings.silencedAlertFeeds, feed.name]))
                      : settings.silencedAlertFeeds.filter((n) => n !== feed.name);
                    setSettings({ ...settings, silencedAlertFeeds: next });
                  }}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    Lyddæmp alarm for {feed.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {feed.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="mt-6 bg-accent text-accent-foreground hover:brightness-110 disabled:opacity-50"
        >
          <Save size={16} className="mr-2" />
          {saveMutation.isPending ? "Gemmer..." : "Gem alarm-indstillinger"}
        </Button>
      </div>

      {/* Feed URLs */}
      <div className="mt-6 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <h2 className="text-lg font-semibold text-foreground">Feed URLs</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Brug disse URLs i Google Merchant Center og Facebook Commerce Manager
        </p>

        <div className="mt-4 space-y-4">
          <FeedUrlRow
            label="Google Shopping Feed"
            path="/feeds/google"
            domain={settings.domain}
            onCopy={() => copyUrl("/feeds/google")}
          />
          <FeedUrlRow
            label={`Facebook Catalog Feed (${
              FACEBOOK_FEED_FORMATS.find(
                (f) => f.value === settings.facebookFormat,
              )?.shortLabel ?? settings.facebookFormat
            })`}
            path="/feeds/facebook"
            domain={settings.domain}
            onCopy={() => copyUrl("/feeds/facebook")}
          />
        </div>
      </div>

      {/* Preflight: validate cars against the active Facebook feed format */}
      <div className="mt-6 max-w-4xl">
        <FeedPreflightCard activeFormat={settings.facebookFormat} />
      </div>

      {/* Preview download: byte-identical XML/JSON for external validators */}
      <div className="mt-6 max-w-4xl">
        <FeedPreviewDownloadCard />
      </div>

      {/* Per-feed collapsible spec: shows fields exported to each platform */}
      <div className="mt-6 max-w-4xl">
        <FeedSpecCard domain={settings.domain} />
      </div>

      {/* Dealer geo diagnostics: shows whether feeds will include lat/lng */}
      <div className="mt-6 max-w-2xl">
        <DealerGeoDiagnosticsCard />
      </div>

      {/* JSON-LD debug: preview homepage + listing Schema.org payloads */}
      <div className="mt-6 max-w-4xl">
        <JsonLdDebugCard />
      </div>

      {/* Feed-ingest health: scheduled refresh history + manual trigger */}
      <div className="mt-6">
        <FeedRefreshHistoryCard />
      </div>
    </div>
  );
}

function FeedUrlRow({
  label,
  path,
  domain,
  onCopy,
}: {
  label: string;
  path: string;
  domain: string;
  onCopy: () => void;
}) {
  const url = `https://${domain}${path}`;
  return (
    <div className="flex items-center gap-3 rounded-lg bg-muted p-4">
      <Rss size={18} className="shrink-0 text-accent" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{url}</p>
      </div>
      <button
        onClick={onCopy}
        className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-background hover:text-foreground"
        title="Kopiér URL"
      >
        <Copy size={16} />
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-background hover:text-foreground"
        title="Åbn feed"
      >
        <ExternalLink size={16} />
      </a>
    </div>
  );
}

/** Inline status banner for the (vertical, format) compatibility check.
 *  Renders an OK note on the happy path; mismatches surface as warning or
 *  blocking banners with a one-click "fix" that swaps the format for the
 *  recommended one. */
function CompatibilityBanner({
  status,
  message,
  recommendedFormat,
  onApplyRecommended,
}: {
  status: "ok" | "warn" | "block";
  message: string;
  recommendedFormat: FacebookFeedFormat | null;
  onApplyRecommended: () => void;
}) {
  if (status === "ok") {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-500/10 p-3 text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
        <p className="text-xs leading-relaxed">{message}</p>
      </div>
    );
  }

  const tone =
    status === "block"
      ? "bg-destructive/10 text-destructive"
      : "bg-amber-500/10 text-amber-700 dark:text-amber-300";
  const Icon = status === "block" ? ShieldAlert : AlertTriangle;
  const recommendedLabel = recommendedFormat
    ? FACEBOOK_FEED_FORMATS.find((f) => f.value === recommendedFormat)?.shortLabel ??
      recommendedFormat
    : null;

  return (
    <div className={`mt-3 flex items-start gap-2 rounded-lg p-3 ${tone}`}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1 text-xs leading-relaxed">
        <p>{message}</p>
        {recommendedFormat ? (
          <button
            type="button"
            onClick={onApplyRecommended}
            className="mt-2 inline-flex items-center gap-1 rounded-md bg-background px-2 py-1 text-xs font-medium ring-1 ring-current hover:brightness-95"
          >
            Skift feed-format til {recommendedLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
